import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { GeocoderAddressComponent } from '../../../UI/PlacesAutocomplete/PlacesAutocomplete.tsx';
import { JURISDICTIONS } from '../../../../constants/jurisdictions.ts';
import { parseListedZoneJurisdictionId, listedZoneJurisdictionLabel } from '../../../../utils/listedZoneJurisdiction.ts';
import {
  mapGoogleProvinceToJurisdiction,
  getLocalityFromAddressComponents,
  sanitizeFormattedAddressForDisplay,
} from '../../../../utils/jurisdictionFromPlace.ts';
import type { CommerceFormPrefill } from '../../../../utils/commercePrefill.ts';
import {
  buildCommerceMaterialSingleItemConfirmRow,
  isAcceptedPriceListFile,
  type CommerceMaterialSingleItemCreateInput,
} from '../../../../services/materialCommerceSheetService.ts';
import { CommerceMaterialsCatalogAddModal } from '../../../Perfil/PerfilMain/MisMaterialesComercio/CommerceMaterialsCatalogAddModal.tsx';
import {
  getCommerceJurisdictionDepartments,
  type CommerceDeliveryScope,
  type CommerceDepartmentCity,
  type CommerceFormEditSeed,
  type ListedZonesByJurisdictionBucketDto,
} from '../../../../services/commerceService.ts';
import listaPickStyles from '../../../../pages/Comercios/CommerceAltaPage.module.scss';
import styles from './CommerceForm.module.scss';
import { buildListedZonesByJurisdictionBuckets, stableListedBucketsJson } from './commerceFormListedBuckets.ts';
import {
  listedZonesInitial,
  listedZonesReducer,
  profileZoneFromListedZones,
} from './commerceFormListedZonesReducer.ts';
import { RADIUS_KM_DEFAULT, clampRadiusKm } from './commerceFormRadiusUtils.ts';
import { reverseGeocodeLatLng } from './commerceFormReverseGeocode.ts';
import { computeCommerceFormValidation, scrollCommerceFormFieldIntoView } from './commerceFormValidation.ts';
import type { CommerceFormSubmitPayload } from './CommerceFormTypes.ts';
import CommerceFormDetailsSection from './CommerceFormDetailsSection.tsx';
import CommerceFormDeliverySection from './CommerceFormDeliverySection.tsx';
import CommerceFormCatalogSection from './CommerceFormCatalogSection.tsx';
import CommerceFormFieldErrors from './CommerceFormFieldErrors.tsx';

export type { CommerceFormSubmitPayload } from './CommerceFormTypes.ts';

interface CommerceFormProps {
  prefill: CommerceFormPrefill;
  userId: number;
  disabled?: boolean;
  cancelLabel?: string;
  variant?: 'alta' | 'embedded';
  mode?: 'create' | 'edit';
  editSeed?: CommerceFormEditSeed | null;
  submitLabel?: string;
  flexibleSheetEnabled?: boolean;
  requireMaterialPrecarga?: boolean;
  suppressCatalogBlocks?: boolean;
  onSkip: () => void;
  onSubmit: (payload: CommerceFormSubmitPayload) => void;
}

const CommerceForm: React.FC<CommerceFormProps> = ({
  prefill,
  userId,
  disabled = false,
  cancelLabel = 'Omitir',
  variant = 'alta',
  mode = 'create',
  editSeed = null,
  submitLabel: submitLabelProp,
  flexibleSheetEnabled = true,
  requireMaterialPrecarga = false,
  suppressCatalogBlocks = false,
  onSkip,
  onSubmit,
}) => {
  const showPriceSheetBlock = variant === 'alta' && mode === 'create';
  const submitLabel =
    submitLabelProp ?? (mode === 'edit' ? 'Guardar cambios' : 'Guardar comercio');
  const [name, setName] = useState(() => String(prefill.name ?? '').trim());
  const [description, setDescription] = useState(() => String(prefill.description ?? '').trim());
  const [zone, setZone] = useState(prefill.zone);
  const [placeInput, setPlaceInput] = useState(prefill.placeInput || prefill.zone);
  const [jurisdiccionId, setJurisdiccionId] = useState<number | ''>(prefill.jurisdiccionId === null ? '' : prefill.jurisdiccionId ?? '');
  const [deliveryScope, setDeliveryScope] = useState<CommerceDeliveryScope>('LOCAL');
  const [deliveryRadiusKm, setDeliveryRadiusKm] = useState(RADIUS_KM_DEFAULT);
  const [mapLat, setMapLat] = useState<number | null>(null);
  const [mapLng, setMapLng] = useState<number | null>(null);
  const [listedZones, dispatchListedZones] = useReducer(listedZonesReducer, listedZonesInitial);
  const [idToListedJurisdiction, setIdToListedJurisdiction] = useState<Record<string, number>>({});
  const [departments, setDepartments] = useState<CommerceDepartmentCity[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState('');
  const [listedZonesSearch, setListedZonesSearch] = useState('');
  const [delivery, setDelivery] = useState(false);
  const [deliveryCondition, setDeliveryCondition] = useState('');
  const [urlSite, setUrlSite] = useState(prefill.url_site_web);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [priceSheet, setPriceSheet] = useState<File | null>(null);
  const [priceSheetError, setPriceSheetError] = useState('');
  const [isDragOverSheet, setIsDragOverSheet] = useState(false);
  const [pendingCatalogMaterials, setPendingCatalogMaterials] = useState<
    CommerceMaterialSingleItemCreateInput[]
  >([]);
  const [precargaModalOpen, setPrecargaModalOpen] = useState(false);
  const [submitFieldIssues, setSubmitFieldIssues] = useState<string[]>([]);
  const [createValidationLock, setCreateValidationLock] = useState(false);
  const sheetInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const pickSheetButtonRef = useRef<HTMLButtonElement>(null);
  const prevDeliveryScopeForListedRef = useRef<CommerceDeliveryScope | null>(null);
  const prevDeliveryScopeForZoneRef = useRef(deliveryScope);
  const lastLocalProfileZoneRef = useRef('');

  useEffect(() => {
    if (!logo) {
      setLogoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(logo);
    setLogoPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [logo]);

  const logoCircleSrc = useMemo(() => {
    if (logoPreviewUrl) return logoPreviewUrl;
    if (logo) return null;
    const u = prefill.logoUrl?.trim();
    return u && u.length > 0 ? u : null;
  }, [logoPreviewUrl, logo, prefill.logoUrl]);

  useEffect(() => {
    setName(String(prefill.name ?? '').trim());
    setDescription(String(prefill.description ?? '').trim());
    setZone(prefill.zone);
    setPlaceInput(prefill.placeInput || prefill.zone);
    setJurisdiccionId(prefill.jurisdiccionId === null ? '' : prefill.jurisdiccionId ?? '');
    setUrlSite(prefill.url_site_web);
  }, [
    prefill.name,
    prefill.description,
    prefill.zone,
    prefill.placeInput,
    prefill.jurisdiccionId,
    prefill.url_site_web,
  ]);

  const editSeedKey = useMemo(
    () => (mode === 'edit' && editSeed ? JSON.stringify(editSeed) : ''),
    [editSeed, mode]
  );

  useEffect(() => {
    if (mode !== 'edit' || !editSeed) return;
    setDelivery(editSeed.delivery);
    setDeliveryCondition(editSeed.delivery_condition);
    setDeliveryScope(editSeed.deliveryScope);
    setDeliveryRadiusKm(clampRadiusKm(editSeed.deliveryRadiusKm));
    setMapLat(editSeed.mapLat);
    setMapLng(editSeed.mapLng);
    dispatchListedZones({
      type: 'replace',
      ids: editSeed.listedZones.ids,
      labels: editSeed.listedZones.labels,
    });
    if (editSeed.listedZonesByJurisdiction != null && editSeed.listedZonesByJurisdiction.length > 0) {
      const m: Record<string, number> = {};
      for (const b of editSeed.listedZonesByJurisdiction) {
        for (const id of b.georefZonaIds) m[id] = b.jurisdiccionId;
      }
      setIdToListedJurisdiction(m);
    } else {
      const m: Record<string, number> = {};
      const pj =
        prefill.jurisdiccionId !== null &&
          prefill.jurisdiccionId !== '' &&
          prefill.jurisdiccionId !== undefined
          ? Number(prefill.jurisdiccionId)
          : NaN;
      if (Number.isFinite(pj) && editSeed.listedZones.ids.length > 0) {
        for (const id of editSeed.listedZones.ids) m[id] = pj;
      }
      setIdToListedJurisdiction(m);
    }
    if (editSeed.deliveryScope === 'LOCAL' && prefill.zone.trim()) {
      lastLocalProfileZoneRef.current = prefill.zone.trim();
    }
  }, [editSeed, editSeedKey, mode, prefill.jurisdiccionId, prefill.zone]);

  useEffect(() => {
    const prev = prevDeliveryScopeForZoneRef.current;
    prevDeliveryScopeForZoneRef.current = deliveryScope;
    if (deliveryScope === 'NATIONWIDE') {
      setZone('Cobertura nacional');
      return;
    }
    if (deliveryScope === 'LISTED_ZONES') {
      setZone(profileZoneFromListedZones(listedZones.ids, listedZones.labels));
      return;
    }
    if (deliveryScope === 'LOCAL' && prev !== 'LOCAL') {
      if (lastLocalProfileZoneRef.current) {
        setZone(lastLocalProfileZoneRef.current);
      } else {
        const fb = placeInput.trim();
        if (fb) setZone(fb);
      }
    }
  }, [deliveryScope, listedZones.ids, listedZones.labels, placeInput]);

  useEffect(() => {
    const prev = prevDeliveryScopeForListedRef.current;
    if (deliveryScope !== 'LISTED_ZONES' && prev === 'LISTED_ZONES') {
      dispatchListedZones({ type: 'clear' });
      setIdToListedJurisdiction({});
      setListedZonesSearch('');
      setDepartmentsError('');
    }
    prevDeliveryScopeForListedRef.current = deliveryScope;
  }, [deliveryScope]);

  useEffect(() => {
    if (deliveryScope !== 'LISTED_ZONES' || jurisdiccionId === '' || jurisdiccionId === null) {
      setDepartments([]);
      setDepartmentsLoading(false);
      return;
    }
    let cancelled = false;
    setDepartmentsLoading(true);
    setDepartmentsError('');
    void getCommerceJurisdictionDepartments(Number(jurisdiccionId))
      .then((res) => {
        if (cancelled) return;
        const cities = res.data?.data?.cities;
        setDepartments(Array.isArray(cities) ? cities : []);
      })
      .catch(() => {
        if (!cancelled) {
          setDepartmentsError('No pudimos cargar el listado de zonas. Probá de nuevo o cambiá de provincia.');
          setDepartments([]);
        }
      })
      .finally(() => {
        if (!cancelled) setDepartmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deliveryScope, jurisdiccionId]);

  const listedZonesIdsKey = useMemo(() => listedZones.ids.join('\u0000'), [listedZones.ids]);

  useEffect(() => {
    if (!departments.length || !listedZones.ids.length) return;
    dispatchListedZones({ type: 'mergeLabels', cities: departments });
  }, [departments, listedZones.ids.length, listedZonesIdsKey]);

  const filteredDepartments = useMemo(() => {
    const q = listedZonesSearch.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((c) => c.name.toLowerCase().includes(q));
  }, [departments, listedZonesSearch]);

  const departmentIdSet = useMemo(() => new Set(departments.map((c) => c.id)), [departments]);

  const listedZonesGrouped = useMemo(() => {
    const byJur = new Map<number, { provinceName: string; ids: string[] }>();
    const other: string[] = [];
    for (const id of listedZones.ids) {
      let jid: number | null = idToListedJurisdiction[id] ?? null;
      if (jid == null) jid = parseListedZoneJurisdictionId(id);
      if (
        jid == null &&
        jurisdiccionId !== '' &&
        jurisdiccionId !== null &&
        departmentIdSet.has(id)
      ) {
        jid = Number(jurisdiccionId);
      }
      if (jid == null) {
        other.push(id);
        continue;
      }
      const provinceName = listedZoneJurisdictionLabel(jid);
      const cur = byJur.get(jid);
      if (cur) cur.ids.push(id);
      else byJur.set(jid, { provinceName, ids: [id] });
    }
    const rows = Array.from(byJur.entries())
      .sort((a, b) => a[1].provinceName.localeCompare(b[1].provinceName, 'es'))
      .map(([jurisdictionId, { provinceName, ids }]) => ({
        jurisdictionId,
        provinceName,
        ids,
      }));
    if (other.length) {
      rows.push({ jurisdictionId: -1, provinceName: 'Otras', ids: other });
    }
    return rows;
  }, [listedZones.ids, departmentIdSet, jurisdiccionId, idToListedJurisdiction]);

  useEffect(() => {
  }, [departments, filteredDepartments, listedZones.ids, listedZonesSearch]);

  useEffect(() => {
  }, [jurisdiccionId, deliveryScope]);

  const toggleListedZone = useCallback(
    (id: string, name?: string) => {
      const wasSelected = listedZones.ids.includes(id);
      if (wasSelected) {
        setIdToListedJurisdiction((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      } else {
        if (jurisdiccionId === '' || jurisdiccionId === null) return;
        setIdToListedJurisdiction((prev) => ({ ...prev, [id]: Number(jurisdiccionId) }));
      }
      dispatchListedZones({ type: 'toggle', id, name });
    },
    [listedZones.ids, jurisdiccionId]
  );

  const removeListedZone = useCallback((id: string) => {
    setIdToListedJurisdiction((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    dispatchListedZones({ type: 'remove', id });
  }, []);

  const applyLocalCoveragePoint = useCallback(
    (place: {
      address: string;
      lat: number;
      lng: number;
      addressComponents?: GeocoderAddressComponent[];
    }) => {
      const rawAddr = place.address.trim();
      const displayAddr = rawAddr
        ? sanitizeFormattedAddressForDisplay(rawAddr)
        : `${place.lat.toFixed(5)}, ${place.lng.toFixed(5)}`;
      setPlaceInput(displayAddr || `${place.lat.toFixed(5)}, ${place.lng.toFixed(5)}`);
      const inferred = mapGoogleProvinceToJurisdiction(place.addressComponents, JURISDICTIONS);
      const locality = getLocalityFromAddressComponents(place.addressComponents);
      if (inferred != null) {
        setJurisdiccionId(inferred);
      }
      const z =
        [locality, displayAddr].filter(Boolean).join(' · ') || displayAddr || rawAddr || place.address;
      lastLocalProfileZoneRef.current = z;
      setZone(z);
      setMapLat(place.lat);
      setMapLng(place.lng);
    },
    []
  );

  const onPlaceSelect = useCallback(
    (place: {
      address: string;
      lat: number;
      lng: number;
      addressComponents?: GeocoderAddressComponent[];
    }) => {
      applyLocalCoveragePoint(place);
    },
    [applyLocalCoveragePoint]
  );

  const handleMapPositionChange = useCallback(
    (lat: number, lng: number) => {
      setMapLat(lat);
      setMapLng(lng);
      const g = (window as unknown as { google?: { maps: any } }).google?.maps;
      if (!g?.Geocoder) {
        setPlaceInput(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        setJurisdiccionId('');
        return;
      }
      void reverseGeocodeLatLng(g, lat, lng).then((res) => {
        if (res?.address) {
          applyLocalCoveragePoint({
            address: res.address,
            lat,
            lng,
            addressComponents: res.addressComponents,
          });
        } else {
          setPlaceInput(`${lat.toFixed(5)}, ${lng.toFixed(5)}`);
          setJurisdiccionId('');
        }
      });
    },
    [applyLocalCoveragePoint]
  );

  const applyPriceSheetFile = useCallback((file: File | null) => {
    setPriceSheetError('');
    if (!file) {
      setPriceSheet(null);
      return;
    }
    if (!isAcceptedPriceListFile(file)) {
      setPriceSheet(null);
      setPriceSheetError(
        'Formato no admitido. Podés subir PDF, imagen (JPEG, PNG, WebP), texto plano, JSON, CSV o Excel (XLS/XLSX).'
      );
      return;
    }
    setPriceSheet(file);
  }, []);

  const onHiddenSheetChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    applyPriceSheetFile(f);
    e.target.value = '';
  }, [applyPriceSheetFile]);

  const onSheetDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!flexibleSheetEnabled || disabled) return;
      setIsDragOverSheet(true);
    },
    [disabled, flexibleSheetEnabled]
  );

  const onSheetDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setIsDragOverSheet(false);
  }, []);

  const onSheetDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onSheetDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOverSheet(false);
      if (!flexibleSheetEnabled || disabled) return;
      const f = e.dataTransfer.files?.[0];
      if (f) applyPriceSheetFile(f);
    },
    [applyPriceSheetFile, disabled, flexibleSheetEnabled]
  );

  const normJuris = (j: number | '' | null | undefined) =>
    j === '' || j === null || j === undefined ? '' : Number(j);

  const listedMaterialIdsPrecarga = useMemo(
    () => new Set(pendingCatalogMaterials.map((m) => m.materialId)),
    [pendingCatalogMaterials]
  );

  const materialPrecargaGateApplies =
    requireMaterialPrecarga && variant === 'alta' && mode === 'create';

  const materialPrecargaReady = useMemo(() => {
    if (!materialPrecargaGateApplies) return true;
    const fileOk =
      flexibleSheetEnabled && priceSheet != null && priceSheetError === '';
    return fileOk || pendingCatalogMaterials.length > 0;
  }, [
    materialPrecargaGateApplies,
    flexibleSheetEnabled,
    priceSheet,
    priceSheetError,
    pendingCatalogMaterials.length,
  ]);

  useEffect(() => {
    if (suppressCatalogBlocks) {
      document.getElementById('commerce-name')?.focus();
      return;
    }
    if (materialPrecargaGateApplies) {
      document.getElementById('commerce-name')?.focus();
      return;
    }
    if (showPriceSheetBlock && flexibleSheetEnabled) {
      pickSheetButtonRef.current?.focus();
      return;
    }
    document.getElementById('commerce-name')?.focus();
  }, [materialPrecargaGateApplies, flexibleSheetEnabled, showPriceSheetBlock, suppressCatalogBlocks]);

  const isEditDirty = useMemo(() => {
    if (mode !== 'edit' || !editSeed) return false;
    if (logo != null) return true;
    if (name.trim() !== prefill.name.trim()) return true;
    if (description.trim() !== prefill.description.trim()) return true;
    if (zone.trim() !== prefill.zone.trim()) return true;
    if (normJuris(jurisdiccionId) !== normJuris(prefill.jurisdiccionId)) return true;
    if (urlSite.trim() !== (prefill.url_site_web ?? '').trim()) return true;
    if (delivery !== editSeed.delivery) return true;
    if (deliveryCondition.trim() !== (editSeed.delivery_condition ?? '').trim()) return true;
    if (deliveryScope !== editSeed.deliveryScope) return true;
    if (deliveryScope === 'LOCAL') {
      if (clampRadiusKm(deliveryRadiusKm) !== clampRadiusKm(editSeed.deliveryRadiusKm)) return true;
      if (mapLat !== editSeed.mapLat || mapLng !== editSeed.mapLng) return true;
    }
    if (deliveryScope === 'LISTED_ZONES') {
      const fj = jurisdiccionId === '' || jurisdiccionId === null ? 0 : Number(jurisdiccionId);
      const curBuckets = stableListedBucketsJson(
        buildListedZonesByJurisdictionBuckets(listedZones.ids, idToListedJurisdiction, fj)
      );
      const baseBuckets =
        editSeed.listedZonesByJurisdiction != null && editSeed.listedZonesByJurisdiction.length > 0
          ? stableListedBucketsJson(editSeed.listedZonesByJurisdiction)
          : stableListedBucketsJson(
            buildListedZonesByJurisdictionBuckets(
              editSeed.listedZones.ids,
              {},
              prefill.jurisdiccionId !== null &&
                prefill.jurisdiccionId !== '' &&
                prefill.jurisdiccionId !== undefined
                ? Number(prefill.jurisdiccionId)
                : 0
            )
          );
      if (curBuckets !== baseBuckets) return true;
    }
    return false;
  }, [
    mode,
    editSeed,
    prefill.name,
    prefill.description,
    prefill.zone,
    prefill.jurisdiccionId,
    prefill.url_site_web,
    logo,
    name,
    description,
    zone,
    jurisdiccionId,
    urlSite,
    delivery,
    deliveryCondition,
    deliveryScope,
    deliveryRadiusKm,
    mapLat,
    mapLng,
    listedZones.ids,
    idToListedJurisdiction,
  ]);

  const createFieldValidation = useMemo(
    () =>
      computeCommerceFormValidation({
        logoFile: logo,
        prefillLogoUrl: prefill.logoUrl,
        name,
        description,
        deliveryScope,
        listedZonesIds: listedZones.ids,
        zone,
        jurisdiccionId,
        deliveryRadiusKm,
        idToListedJurisdiction,
      }),
    [
      logo,
      prefill.logoUrl,
      name,
      description,
      deliveryScope,
      listedZones.ids,
      zone,
      jurisdiccionId,
      deliveryRadiusKm,
      idToListedJurisdiction,
    ]
  );

  useEffect(() => {
    if (!createFieldValidation.ok) return;
    setSubmitFieldIssues([]);
  }, [createFieldValidation.ok]);

  const handleSubmit = useCallback(() => {
    const validation = computeCommerceFormValidation({
      logoFile: logo,
      prefillLogoUrl: prefill.logoUrl,
      name,
      description,
      deliveryScope,
      listedZonesIds: listedZones.ids,
      zone,
      jurisdiccionId,
      deliveryRadiusKm,
      idToListedJurisdiction,
    });

    if (!validation.ok) {
      setSubmitFieldIssues(validation.issues);
      if (mode === 'create') {
        setCreateValidationLock(true);
      }
      if (validation.firstFocusId) {
        scrollCommerceFormFieldIntoView(validation.firstFocusId);
      }
      return;
    }

    setSubmitFieldIssues([]);
    if (mode === 'create') {
      setCreateValidationLock(false);
    }

    let listedZonesByJurisdictionPayload: ListedZonesByJurisdictionBucketDto[] | undefined;
    if (deliveryScope === 'LISTED_ZONES') {
      const fj = Number(jurisdiccionId);
      const bucketDtos = buildListedZonesByJurisdictionBuckets(
        listedZones.ids,
        idToListedJurisdiction,
        fj
      ).filter((b) => b.georefZonaIds.length > 0);
      listedZonesByJurisdictionPayload = bucketDtos;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      zone: zone.trim(),
      delivery,
      delivery_condition: deliveryCondition.trim() || undefined,
      url_site_web: urlSite.trim() || undefined,
      jurisdiccionId: Number(jurisdiccionId),
      userId,
      logo,
      priceSheet:
        suppressCatalogBlocks ? null : showPriceSheetBlock && flexibleSheetEnabled ? priceSheet : null,
      deliveryScope,
      ...(deliveryScope === 'LOCAL'
        ? {
          deliveryRadiusKm,
          ...(mapLat != null && mapLng != null ? { latitude: mapLat, longitude: mapLng } : {}),
        }
        : {}),
      ...(deliveryScope === 'LISTED_ZONES' && listedZonesByJurisdictionPayload
        ? { listedZonesByJurisdiction: listedZonesByJurisdictionPayload }
        : {}),
      ...(variant === 'alta' && mode === 'create' && !suppressCatalogBlocks
        ? { pendingCatalogMaterials }
        : {}),
    });
  }, [
    name,
    description,
    zone,
    jurisdiccionId,
    delivery,
    deliveryCondition,
    urlSite,
    userId,
    logo,
    priceSheet,
    flexibleSheetEnabled,
    showPriceSheetBlock,
    suppressCatalogBlocks,
    onSubmit,
    deliveryScope,
    deliveryRadiusKm,
    mapLat,
    mapLng,
    listedZones,
    idToListedJurisdiction,
    variant,
    mode,
    pendingCatalogMaterials,
    prefill.logoUrl,
  ]);

  const showInvalidFieldChrome =
    !createFieldValidation.ok &&
    (submitFieldIssues.length > 0 || (mode === 'create' && createValidationLock));

  const isFieldInvalid = (fieldId: string) =>
    showInvalidFieldChrome && createFieldValidation.invalidFieldIds.includes(fieldId);

  return (
    <motion.div
      className={styles.form}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <CommerceFormDetailsSection
        styles={styles as Record<string, string>}
        disabled={disabled}
        isFieldInvalid={isFieldInvalid}
        logoInputRef={logoInputRef}
        logo={logo}
        setLogo={setLogo}
        logoCircleSrc={logoCircleSrc}
        prefillLogoUrl={prefill.logoUrl}
        name={name}
        setName={setName}
        delivery={delivery}
        setDelivery={setDelivery}
        urlSite={urlSite}
        setUrlSite={setUrlSite}
        deliveryCondition={deliveryCondition}
        setDeliveryCondition={setDeliveryCondition}
        description={description}
        setDescription={setDescription}
      >
        <CommerceFormDeliverySection
          styles={styles as Record<string, string>}
          disabled={disabled}
          isFieldInvalid={isFieldInvalid}
          deliveryScope={deliveryScope}
          setDeliveryScope={setDeliveryScope}
          deliveryRadiusKm={deliveryRadiusKm}
          setDeliveryRadiusKm={setDeliveryRadiusKm}
          mapLat={mapLat}
          mapLng={mapLng}
          onMapPositionChange={handleMapPositionChange}
          placeInput={placeInput}
          setPlaceInput={setPlaceInput}
          onPlaceSelect={onPlaceSelect}
          listedZones={listedZones}
          jurisdiccionId={jurisdiccionId}
          setJurisdiccionId={setJurisdiccionId}
          departments={departments}
          departmentsLoading={departmentsLoading}
          departmentsError={departmentsError}
          listedZonesSearch={listedZonesSearch}
          setListedZonesSearch={setListedZonesSearch}
          filteredDepartments={filteredDepartments}
          listedZonesGrouped={listedZonesGrouped}
          toggleListedZone={toggleListedZone}
          removeListedZone={removeListedZone}
        />
      </CommerceFormDetailsSection>
      <CommerceFormCatalogSection
        styles={styles as Record<string, string>}
        listaPickStyles={listaPickStyles as Record<string, string>}
        suppressCatalogBlocks={suppressCatalogBlocks}
        materialPrecargaGateApplies={materialPrecargaGateApplies}
        flexibleSheetEnabled={flexibleSheetEnabled}
        showPriceSheetBlock={showPriceSheetBlock}
        disabled={disabled}
        priceSheet={priceSheet}
        priceSheetError={priceSheetError}
        isDragOverSheet={isDragOverSheet}
        sheetInputRef={sheetInputRef}
        pickSheetButtonRef={pickSheetButtonRef}
        onSheetDragEnter={onSheetDragEnter}
        onSheetDragLeave={onSheetDragLeave}
        onSheetDragOver={onSheetDragOver}
        onSheetDrop={onSheetDrop}
        onHiddenSheetChange={onHiddenSheetChange}
        applyPriceSheetFile={applyPriceSheetFile}
        pendingCatalogMaterials={pendingCatalogMaterials}
        setPendingCatalogMaterials={setPendingCatalogMaterials}
        onOpenPrecargaModal={() => setPrecargaModalOpen(true)}
      />
      <CommerceFormFieldErrors issues={submitFieldIssues} styles={styles as Record<string, string>} />
      <div className={styles.actions}>
        <button type="button" className={styles.cancel} disabled={disabled} onClick={onSkip}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={styles.submit}
          disabled={
            disabled ||
            (mode === 'create' && createValidationLock && !createFieldValidation.ok) ||
            (mode === 'edit' && !isEditDirty) ||
            (materialPrecargaGateApplies && !materialPrecargaReady)
          }
          onClick={handleSubmit}
        >
          {submitLabel}
        </button>
      </div>
      {materialPrecargaGateApplies && !suppressCatalogBlocks ? (
        <CommerceMaterialsCatalogAddModal
          isOpen={precargaModalOpen}
          onClose={() => setPrecargaModalOpen(false)}
          listedMaterialIds={listedMaterialIdsPrecarga}
          disableActions={disabled}
          onAddItem={async (input) => {
            buildCommerceMaterialSingleItemConfirmRow(input);
            setPendingCatalogMaterials((prev) => [...prev, input]);
          }}
        />
      ) : null}
    </motion.div>
  );

};

export default CommerceForm;
